import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { Trash2, RotateCcw, Inbox } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SkeletonListItem } from "../components/Skeleton";
import {
  getDeletedDeals,
  permanentlyDeleteDeal,
  restoreDeal,
  getAlldealforAdmin,
} from "../utils/firebasefunctions";
import { useStateValue } from "../context/StateProvider";
import { actionType } from "../context/reducer";

const RETENTION_DAYS = 30;
const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

function formatCurrency(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export default function RecentlyDeleted() {
  const [, dispatch] = useStateValue();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const items = await getDeletedDeals();
      const now = Date.now();
      const expired = items.filter((d) => now - d.deletedAt > RETENTION_MS);
      const kept = items.filter((d) => now - d.deletedAt <= RETENTION_MS);

      // Auto-purge expired items (best-effort, runs whenever this page is viewed).
      for (const exp of expired) {
        try {
          await permanentlyDeleteDeal(exp.createdat);
        } catch (err) {
          console.warn("Auto-purge failed for", exp.createdat, err);
        }
      }
      setRows(kept);
    } catch (err) {
      console.error("Failed to load recently deleted:", err);
      toast.error("Could not load Recently Deleted");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const syncDealsContext = async () => {
    try {
      const fresh = await getAlldealforAdmin("deals");
      dispatch({ type: actionType.STORE_DEALS, deals: fresh });
    } catch (err) {
      console.warn("Failed to refresh deals after restore/purge:", err);
    }
  };

  const handleRestore = async (deal) => {
    setBusyId(deal.createdat);
    try {
      const ok = await restoreDeal(deal.createdat);
      if (!ok) {
        toast.error("Could not restore deal");
        return;
      }
      toast.success("Deal restored");
      setRows((prev) => prev.filter((d) => d.createdat !== deal.createdat));
      syncDealsContext();
    } finally {
      setBusyId(null);
    }
  };

  const handlePurge = async (deal) => {
    if (
      !window.confirm(
        `Permanently delete "${deal.propertyaddress || "this deal"}"? This cannot be undone.`,
      )
    )
      return;
    setBusyId(deal.createdat);
    try {
      const ok = await permanentlyDeleteDeal(deal.createdat);
      if (!ok) {
        toast.error("Could not delete deal");
        return;
      }
      toast.success("Deleted forever");
      setRows((prev) => prev.filter((d) => d.createdat !== deal.createdat));
    } finally {
      setBusyId(null);
    }
  };

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    if (rows.length === 0) return "Trash is empty";
    return `${rows.length} item${rows.length === 1 ? "" : "s"} · auto-purged after ${RETENTION_DAYS} days`;
  }, [rows.length, loading]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader title="Recently Deleted" subtitle={subtitle} />

      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6">
        {loading ? (
          <ul className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <li
                key={i}
                className="bg-white rounded-lg border border-slate-200"
              >
                <SkeletonListItem />
              </li>
            ))}
          </ul>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Inbox size={28} />
            <p className="text-sm mt-2">No deleted deals.</p>
            <p className="text-xs mt-1 text-slate-400">
              Anything you delete will show up here for {RETENTION_DAYS} days.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((deal) => {
              const purgeAt = (deal.deletedAt || 0) + RETENTION_MS;
              const daysLeft = Math.max(
                0,
                Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000)),
              );
              const busy = busyId === deal.createdat;
              return (
                <li
                  key={deal.createdat}
                  className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4"
                >
                  <img
                    src={
                      deal.propertyphoto?.length > 0
                        ? deal.propertyphoto[0]
                        : "/assets/no-image.png"
                    }
                    onError={(e) => {
                      e.currentTarget.src = "/assets/no-image.png";
                    }}
                    className="w-16 h-16 rounded-md object-cover flex-shrink-0 bg-slate-100"
                    alt=""
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      {deal.propertyaddress || "Untitled deal"}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {deal.dealtype ? `${deal.dealtype} · ` : ""}
                      {formatCurrency(deal.offerPrice)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Deleted {moment(deal.deletedAt).fromNow()} ·{" "}
                      {daysLeft} day{daysLeft === 1 ? "" : "s"} left
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRestore(deal)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePurge(deal)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Delete forever
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
